import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import {
  setOfflineCachedProducts,
  setOfflineCachedCategories,
  setOfflineCachedLocations,
  setOfflineCachedBankAccounts,
  setOfflineCachedTenantSettings,
  setOfflineCachedFbrSettings,
  setOfflineCachedPromotions,
  setOfflineCachedSerials,
  type CachedSerial,
} from '@/lib/offline-db';
import { useOnlineStatus } from './useOnlineStatus';
import { useAuthStore } from '@/stores/auth.store';

const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Pre-caches all data the POS terminal needs to function offline.
 * Called from POSPage — runs once on mount (if online) and every 5 minutes.
 * Silently fails — offline cache is best-effort; network errors are swallowed.
 */
export function useOfflineCache() {
  const isOnline = useOnlineStatus();
  const { hasPermission } = useAuthStore();
  const [cacheReady, setCacheReady] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const runningRef = useRef(false);

  const populateCache = async () => {
    if (runningRef.current || !isOnline) return;
    runningRef.current = true;
    try {
      const [products, categories, locations, tenantSettings] = await Promise.allSettled([
        api.get('/products/pos').then((r) => r.data),
        api.get('/categories').then((r) => r.data),
        api.get('/locations').then((r) => {
          const d = r.data;
          return Array.isArray(d) ? d : d?.data ?? [];
        }),
        api.get('/tenant/tax-rule-settings').then((r) => r.data),
      ]);

      if (products.status === 'fulfilled') {
        const data = products.value;
        await setOfflineCachedProducts(Array.isArray(data) ? data : data?.data ?? []);
      }
      if (categories.status === 'fulfilled') {
        const data = categories.value;
        await setOfflineCachedCategories(Array.isArray(data) ? data : data?.data ?? []);
      }
      if (locations.status === 'fulfilled') {
        await setOfflineCachedLocations(locations.value);
      }
      if (tenantSettings.status === 'fulfilled') {
        await setOfflineCachedTenantSettings(tenantSettings.value);
      }

      // Bank accounts (id/name only, cashier-safe options endpoint — T-479)
      try {
        const bankData = await api.get('/bank-accounts/options').then((r) => r.data);
        await setOfflineCachedBankAccounts(Array.isArray(bankData) ? bankData : []);
      } catch { /* ignore */ }

      // Automatic promotions in engine shape, so an offline cart is priced with them (POS-I78)
      try {
        const promos = await api.get('/promotions/pos-engine').then((r) => r.data);
        await setOfflineCachedPromotions(Array.isArray(promos) ? promos : []);
      } catch { /* ignore */ }

      // In-stock serials of serial-tracked products, so a phone can be sold offline (POS-I67)
      if (products.status === 'fulfilled') {
        try {
          const list = Array.isArray(products.value) ? products.value : products.value?.data ?? [];
          const trackedIds: string[] = list
            .filter((p: { serialTracking?: boolean; id: string }) => p.serialTracking)
            .map((p: { id: string }) => p.id);
          const serials: CachedSerial[] = [];
          for (const productId of trackedIds) {
            const res = await api.get('/serial-numbers/available', { params: { productId, limit: 500 } }).then((r) => r.data);
            const rows = Array.isArray(res) ? res : res?.data ?? [];
            for (const row of rows) {
              serials.push({ id: row.id, productId: row.productId ?? productId, serialNo: row.serialNo, status: row.status });
            }
          }
          await setOfflineCachedSerials(serials);
        } catch { /* ignore */ }
      }

      // FBR settings — only if user has FBR permission
      if (hasPermission('fbr:*') || hasPermission('fbr:read')) {
        try {
          const fbrData = await api.get('/fbr/settings').then((r) => r.data);
          await setOfflineCachedFbrSettings(fbrData);
        } catch { /* ignore */ }
      }

      setCacheReady(true);
    } catch {
      // Fail silently — cache is best-effort
    } finally {
      runningRef.current = false;
    }
  };

  useEffect(() => {
    if (isOnline) {
      populateCache();
      timerRef.current = setInterval(populateCache, REFRESH_INTERVAL_MS);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isOnline]); // eslint-disable-line react-hooks/exhaustive-deps

  return { cacheReady };
}
